/**
 * KB-3318 — resolve the badge/lock audience for a governed form from the signed-in user's role.
 *
 * PURE (no React, no I/O) so the mapping is unit-tested without a DOM. Its inputs are exactly the
 * AuthContext values (`useAuth()`): the computed `isAdmin`, the `userProfile` (whose `.role` is one of
 * `admin_user` / `camp_manager` / `camp_contributor` / `camp_user`), the `userRoles` claim array, and
 * — for the welcome flow — the invitation the page was opened with.
 *
 *   • admin           → "staff"   (the six-state matrix + the lock/unlock control)
 *   • camp_manager / camp_contributor (profile role OR claim role), or an invitation context
 *                     → "director" (the three director strings; NO lock control, NO vendor hint)
 *   • anything else, INCLUDING a still-loading profile (null profile, empty roles) or camp_user
 *                     → null      (render no badge and no lock control)
 *
 * Why null rather than a "staff" default for the loading window: profile enrichment runs off the
 * blocking auth gate (`AuthContext.enrichUserSession`), so `userProfile` is null for a window after
 * sign-in. Defaulting to "staff" there would briefly hand a director the lock control on their own
 * camp's fields. `describeBadge`/`FieldOwnershipBadge` render nothing for a null audience.
 *
 * Note this is only the AUDIENCE the form presents. Whether a director's edit actually produces a
 * "director" lock server-side is a separate question the pipeline decides from `users/<uid>.role`
 * (`attributeCanonicalWriteCore.classifyUserRole`); a guest whose users doc carries no role classifies
 * `unknown` and never locks, so they see the director copy but never "Edited by you" (KB-3318 ruling 3).
 */

import type { BadgeAudience } from "./campDataFieldState";

/** The roles the pipeline classifies as a camp director (`classifyUserRole` → "director"). */
const DIRECTOR_ROLES: ReadonlySet<string> = new Set(["camp_manager", "camp_contributor"]);

export interface ResolveAudienceInput {
  /** AuthContext `userProfile` — `null` while enrichment is still in flight. */
  userProfile?: { role?: string | null } | null;
  /** AuthContext `userRoles` — roles from the token claims (+ the fetched profile). */
  userRoles?: readonly string[] | null;
  /** AuthContext `isAdmin` — the computed admin flag (known UIDs, profile role, or claim role). */
  isAdmin?: boolean;
  /** The welcome-flow invitation, when the form was reached through an invitation link. */
  invitation?: unknown | null;
}

/** Resolve the form audience, or null when nothing should render. Pure. */
export function resolveAudience(input: ResolveAudienceInput): BadgeAudience | null {
  const { userProfile, userRoles, isAdmin, invitation } = input;

  // Admin wins absolutely — a real admin with a director role (or previewing an invitation) is staff.
  if (isAdmin) return "staff";

  const profileRole = userProfile?.role;
  const claimRoles = Array.isArray(userRoles) ? userRoles : [];
  const hasDirectorRole =
    (typeof profileRole === "string" && DIRECTOR_ROLES.has(profileRole)) ||
    claimRoles.some((r) => typeof r === "string" && DIRECTOR_ROLES.has(r));
  if (hasDirectorRole) return "director";

  // An invitation link is itself a director context (a guest acting for their camp), even before any
  // role has loaded — the welcome flow is invitation-driven director onboarding.
  if (invitation != null) return "director";

  return null;
}
